<script setup lang="ts">
    import SearchTile from './SearchTile.vue';
    import FrontpageGroup from './FrontpageGroup.vue';
    import { ref, computed, onMounted } from 'vue';
    import { useWindowSize } from '@vueuse/core';
    import favouriteService from '@/shared/services/favouriteService';
    import { http } from '@/shared/httpWrapper';
    import { FrontpageItem, FrontpageItemGroup, getFrontpageItems } from '@/shared/helpers/api';
    import { getLibraryCount } from '@/shared/helpers/libraryCount';
    import { callWithDefaultOnError } from '@/shared/helpers/errorUtils';

    const allItems = ref<FrontpageItemGroup[]>([]);
    const filterInput = ref<string>('');
    const libraryCount = ref<number | null>();
    const { width } = useWindowSize();

    onMounted(async () => {
        if (allItems.value.length === 0) {
            const [groups, favourites, libCount] = await Promise.all([
                callWithDefaultOnError(() => getFrontpageItems(http), []),
                callWithDefaultOnError(() => favouriteService.get(http), []),
                callWithDefaultOnError(() => getLibraryCount(http), 0)
            ]);

            allItems.value = [...groups];

            if (favourites.length > 0) {
                allItems.value.unshift({
                    label: 'My favourites',
                    class: 'fpig-fav',
                    items: favourites.map(x => ({
                        label: x.title,
                        icon: 'star-outline',
                        href: x.url
                    }))
                });
            }

            if (libCount > 0) {
                libraryCount.value = libCount;
            }
        }
    });

    const filterTokens = computed<string[]>(() => {
        const trimmed = filterInput.value.trim();
        if (trimmed.length === 0) {
            return [];
        } else {
            return trimmed
                .split(/\s+/)
                .filter(x => x.length > 0)
                .map(x => x.toLowerCase());
        }
    });

    const filteredGroups = computed<FrontpageItemGroup[]>(() => {
        let result: FrontpageItemGroup[] = [];

        if (filterTokens.value.length === 0) {
            result = allItems.value;
        } else {
            for (const group of allItems.value) {
                if (includedText(group.label, filterTokens.value)) {
                    result.push(group);
                } else {
                    const tempItems: FrontpageItem[] = [];
                    for (const item of group.items) {
                        if (includedText(item.label, filterTokens.value)) {
                            tempItems.push(item);
                        }
                    }
                    if (tempItems.length > 0) {
                        result.push({
                            label: group.label,
                            class: group.class,
                            items: tempItems
                        });
                    }
                }
            }
        }

        return result;
    });

    const itemColumnCount = computed<number>(() => {
        const columnRequirement = 300;
        const unavailable = 620;
        return Math.max(1,
                        Math.min(5,
                                 Math.floor((width.value - unavailable) / columnRequirement)));
    });

    const filteredGroupsColumns = computed<FrontpageItemGroup[][]>(() => {
        const result: FrontpageItemGroup[][] = [];
        const groups = filteredGroups.value;
        const columnCount = itemColumnCount.value;
        const bucketSize = Math.ceil(groups.length / columnCount);

        for (let i = 0; i < columnCount; i++) {
            result.push(groups.slice(i * bucketSize, (i * bucketSize) + bucketSize));
        }

        return result;
    });

    function includedText(text: string, tokens: string[]): boolean {
        if (tokens.length === 0) {
            return true;
        }
        const lowerText = text.toLowerCase();
        return tokens.every(t => lowerText.includes(t));
    }

    const showAllLibrariesHint = computed(() => {
        return filterInput.value.length >= 2;
    });

</script>

<template>
    <div class="columns">
        <div class="column is-narrow">
            <search-tile />
            <p class="has-text-grey has-text-centered">
                Common Library &copy; Equinor ASA 2019 - {{ new Date().getUTCFullYear() }}
            </p>
        </div>
        <div class="column ml-4">
            <b-input
                v-model="filterInput"
                class="mb-4"
                placeholder="Filter items.."
                icon="magnify"
                autofocus />
            <transition name="slide">
                <div
                    v-if="showAllLibrariesHint"
                    class="ml-3 has-text-grey sliding-text">
                    <i><b>Hint:</b> Not finding what you're looking for here? Look in the complete
                        <router-link :to="{ path: '/library', query: { SearchFilter: filterInput } }">
                            list of {{ libraryCount }} libraries.
                        </router-link>
                    </i>
                </div>
            </transition>
            <div class="columns mt-3">
                <div
                    v-for="(column, cIndex) in filteredGroupsColumns"
                    :key="cIndex"
                    class="column">
                    <frontpage-group
                        v-for="(group, index) in column"
                        :key="index"
                        :group="group" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.slide-enter-active {
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.slide-enter-from {
  transform: translateX(200%);
  opacity: 0;
}
</style>
